import { PayloadAction } from '@reduxjs/toolkit';
import { SortOrder } from '../../types';
import { createSliceCustom } from '../../utils/tests/createSliceCustom';
import {
  editQuarterlyRevenueSummary,
  getQuarterlyRevenueAccount,
  getQuarterlyRevenueById,
  updateQuarterlyRevenueSummary,
} from './quarterlyRevenue.thunks';
import {
  AccountsSortPayloadType,
  QuarterlyRevenueSortPayloadType,
  QuarterlyRevenueState,
  quarterlyRevenueStoreKey,
  SortAccountsBy,
  SortQuarterlyRevenueBy,
} from './quarterlyRevenue.types';
import {
  sortAccountsMap,
  sortQuarterlyRevenueMap,
} from './quarterlyRevenue.const';
import { AccountsPageFiltersInitialState } from '../accounts';
import {
  QuarterlyRevenueAccount,
  QuarterlyRevenueAccountSummary,
} from '../../types/quarterlyRevenue';

import { DateTime } from 'luxon';

const ACCOUNT_LEVEL_SORT_BY = [
  SortAccountsBy.AccountName,
  SortAccountsBy.PDM,
  SortAccountsBy.DM,
  SortAccountsBy.PDMAndDM,
];

export const initialState: QuarterlyRevenueState = {
  loading: false,
  error: null,
  accounts: [],
  sortBy: SortAccountsBy.AccountName,
  sortOrder: SortOrder.Ascending,
  sortQuarterlyRevenueBy: SortQuarterlyRevenueBy.Quarter,
  quarterlyRevenueSortOrder: SortOrder.Descending,

  dateFilterValue: null,
  dateFilterValueLocal: DateTime.now().toFormat('yyyy-MM-dd'),
  filters: {
    ...AccountsPageFiltersInitialState,
    nextQRevenueStatus: '',
    clientGrowth: '',
    hcDeltaType: '',
  },
  accountsList: [],
  projectAndAccountListLoading: false,
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  isUniqueFilterListsDefined: false,
  uniqueFilterLists: {
    pdms: [],
    dms: [],
  },
};

export const returns = createSliceCustom({
  name: quarterlyRevenueStoreKey,
  initialState,
  reducers: {
    setSortBy: (state, { payload }: PayloadAction<AccountsSortPayloadType>) => {
      state.sortBy = payload.sortBy;
      state.sortOrder = payload.sortOrder ?? SortOrder.Ascending;
      const sortingFn = sortAccountsMap[state.sortBy];

      // Account-level fields (unlike status-level fields such as revenue
      // status or headcount) apply regardless of whether an account has a
      // status yet, so they sort the full list like AccountName does.
      if (ACCOUNT_LEVEL_SORT_BY.includes(payload.sortBy)) {
        const accounts = state.accounts.sort(sortingFn);

        state.accounts =
          state.sortOrder === SortOrder.Ascending
            ? accounts
            : accounts.reverse();
      } else {
        const accountsWithStatus = state.accounts.filter(
          (account) => account.statuses && account.statuses.length > 0,
        );
        const accountsWithoutStatus = state.accounts.filter(
          (account) => !account.statuses || account.statuses.length === 0,
        );

        state.accounts =
          state.sortOrder === SortOrder.Ascending
            ? [...accountsWithoutStatus, ...accountsWithStatus.sort(sortingFn)]
            : [
                ...accountsWithoutStatus,
                ...accountsWithStatus.sort(sortingFn).reverse(),
              ];
      }
    },
    sortRevenuesBy: (
      state,
      { payload }: PayloadAction<QuarterlyRevenueSortPayloadType>,
    ) => {
      state.sortQuarterlyRevenueBy = payload.sortBy;
      state.quarterlyRevenueSortOrder =
        payload.sortOrder ?? SortOrder.Ascending;
      const sortingFn = sortQuarterlyRevenueMap[state.sortQuarterlyRevenueBy];

      const accounts = state.accountsList.sort(sortingFn);

      state.accountsList =
        state.quarterlyRevenueSortOrder === SortOrder.Ascending
          ? accounts
          : accounts.reverse();
    },
    setRevenuePageFilters: (
      state,
      { payload }: PayloadAction<Partial<QuarterlyRevenueState>>,
    ) => {
      state.filters = { ...state.filters, ...payload };
      state.loading = true;
    },
    resetRevenuePageFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearAccount: (state) => {
      state.accountsList = [];
    },
    cleanAccountsList: (state) => {
      state.accountsList = [];
    },
    setForecastDateLocal: (
      state,
      { payload }: PayloadAction<string | null>,
    ) => {
      state.dateFilterValueLocal = payload;
    },
    setForecastDate: (state) => {
      state.dateFilterValue = state.dateFilterValueLocal;
    },
    cleanForecastDate: (state) => {
      state.dateFilterValue = null;
      state.dateFilterValueLocal = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuarterlyRevenueAccount.pending, (state) => {
        state.loading = true;
        state.error = initialState.error;
        state.accountListFetchErrorMessage = null;
      })
      .addCase(
        getQuarterlyRevenueAccount.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            accounts: QuarterlyRevenueAccount[];
            totalPages: number;
          }>,
        ) => {
          state.loading = false;
          state.totalPages = payload.totalPages;

          const sortingFn = sortAccountsMap[state.sortBy];
          const accounts = payload.accounts.sort(sortingFn);

          state.accounts =
            state.sortOrder === SortOrder.Ascending
              ? accounts
              : accounts.reverse();

          if (state.isUniqueFilterListsDefined === false) {
            state.isUniqueFilterListsDefined = true;

            state.uniqueFilterLists.pdms = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              state.accounts.forEach((item) => {
                if (item.pdmId && !uniqueItems.includes(item.pdmId)) {
                  uniqueItems.push(item.pdmId);
                  filterArray.push({
                    value: item.pdmId,
                    label: item.pdmFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();

            state.uniqueFilterLists.dms = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              state.accounts.forEach((item) => {
                if (item.dmId && !uniqueItems.includes(item.dmId)) {
                  uniqueItems.push(item.dmId);
                  filterArray.push({
                    value: item.dmId,
                    label: item.dmFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();
          }
        },
      )
      .addCase(getQuarterlyRevenueAccount.rejected, (state) => {
        state.loading = false;
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(updateQuarterlyRevenueSummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateQuarterlyRevenueSummary.fulfilled, (state, action) => {
        const source = action.meta.arg.source;
        state.loading = false;
        if (source === 'accounts')
          state.accounts = state.accounts.map((account) =>
            account.accountId === action.payload.accountId
              ? { ...account, statuses: [action.payload] }
              : account,
          );
        else {
          const sortingFn =
            sortQuarterlyRevenueMap[state.sortQuarterlyRevenueBy];

          const accounts = [...state.accountsList, action.payload].sort(
            sortingFn,
          );

          state.accountsList =
            state.quarterlyRevenueSortOrder === SortOrder.Ascending
              ? accounts
              : accounts.reverse();
        }
      })
      .addCase(updateQuarterlyRevenueSummary.rejected, (state) => {
        state.loading = false;
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(editQuarterlyRevenueSummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(editQuarterlyRevenueSummary.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg.source === 'accounts')
          state.accounts = state.accounts.map((account) =>
            account.accountId === action.payload.accountId
              ? { ...account, statuses: [action.payload] }
              : account,
          );
        else
          state.accountsList = state.accountsList.map((account) =>
            account.id === action.payload.id ? action.payload : account,
          );
      })
      .addCase(editQuarterlyRevenueSummary.rejected, (state) => {
        state.loading = false;
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(getQuarterlyRevenueById.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getQuarterlyRevenueById.fulfilled,
        (
          state,
          { payload }: PayloadAction<QuarterlyRevenueAccountSummary[]>,
        ) => {
          state.accountsList = payload;
          state.loading = false;
        },
      )
      .addCase(getQuarterlyRevenueById.rejected, (state) => {
        state.loading = false;
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      });
  },
});

export const quarterlyRevenueSlice = returns.slice;
export const {
  cleanAccountsList,
  clearAccount,
  setForecastDateLocal,
  setForecastDate,
  cleanForecastDate,
  setRevenuePageFilters,
  resetRevenuePageFilters,
  setSortBy,
} = quarterlyRevenueSlice.actions;

export const quarterlyRevenueReducers = returns.reducers;
export const quarterlyRevenueExtraReducers = returns.extraReducers;
